import React from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuItem as HudskitMenuItem } from 'hfs-common-ui';
import { PermissionEnum } from 'fleet-data-model';
import paths from '~/app/paths/paths';
import { InternationalizationNameSpace } from '~/constants/internationalization-namespace';
import urls from '~/constants/urls';
import MenuConstants from '~/constants/menu-constants';
import FormattedMessageWrapper from '~/components/views/FormattedMessageWrapper/FormattedMessageWrapper';
import { checkPermission } from '~/ducks/authentication/selectors';
import { getGreenkeeperNavigationItem } from '~/domain/greenkeeper-tool/utils/greenkeeper-navigation';
import { getDiamGroupId } from '~/domain/uptime/utils/utils';
import type { MenuItem, MultiMenuItem } from './data/MenuState';
import { CompanyInfoLabel } from './components/CompanyInfoLabel';
import type {
  FleetMenuItem,
  IconMenuItem,
  MappedLinkMenuItem,
  MappedMenuItem,
  MappedOutLinkMenuItem,
} from './types/types';
import MenuLabel from './components/MenuLabel/MenuLabel';

interface TranslatedMenuLabelProps {
  translationKey: string;
}

const GreenkeeperMenuLabel = ({ translationKey }: TranslatedMenuLabelProps) => {
  const { t } = useTranslation([InternationalizationNameSpace.GreenkeeperTool]);

  return <>{t(translationKey) as string}</>;
};

const mapGreenkeeperMenuConstants = (id: MenuConstants): MappedMenuItem | undefined => {
  const greenkeeperNavigationItem = getGreenkeeperNavigationItem(id);

  if (greenkeeperNavigationItem) {
    return {
      key: greenkeeperNavigationItem.id,
      path: greenkeeperNavigationItem.path,
      label: <GreenkeeperMenuLabel translationKey={greenkeeperNavigationItem.labelKey} />,
      id: greenkeeperNavigationItem.menuLinkId,
      iconLeft: greenkeeperNavigationItem.icon,
      isOnlyActiveWhenMatchingAll: greenkeeperNavigationItem.isOnlyActiveWhenMatchingAll,
    };
  }

  if (id === MenuConstants.GREENKEEPER) {
    return {
      key: MenuConstants.GREENKEEPER,
      path: paths.greenkeeperRoot.path,
      message: 'web.menu.greenkeeper.link',
      id: 'greenkeeperMenuLink',
    };
  }
};

export const mapMenuConstants = (menuItem: MenuItem): MappedMenuItem | undefined => {
  const greenkeeperMenuItem = mapGreenkeeperMenuConstants(menuItem.id);

  if (greenkeeperMenuItem) {
    return greenkeeperMenuItem;
  }

  switch (menuItem.id) {
    case MenuConstants.DASHBOARD:
      return {
        key: MenuConstants.DASHBOARD,
        path: paths.dashboard.path,
        message: 'web.menu.dashboard.link',
        id: 'dashboardMenuLink',
      };

    case MenuConstants.COMPANIES_COMMERCIAL:
      return {
        key: MenuConstants.COMPANIES_COMMERCIAL,
        path: paths.companies.getPath({
          companyType: 'commercial',
        }),
        message: 'web.menu.companies.link',
        id: 'companiesMenuLink',
      };

    case MenuConstants.INVITE_REQUESTS:
      return {
        key: MenuConstants.INVITE_REQUESTS,
        path: paths.inviteRequests.getPath({
          status: 'approved',
        }),
        message: 'web.menu.inviterequests.link',
        id: 'invitesMenuLink',
      };

    case MenuConstants.MACHINES_ALL:
      return {
        key: MenuConstants.MACHINES_ALL,
        path: paths.machines.getPath(),
        message: 'web.menu.machines.link',
        id: 'machinesMenuLink',
      };

    case MenuConstants.INVENTORY:
      return {
        key: MenuConstants.INVENTORY,
        path: paths.inventory.getPath(),
        message: 'web.menu.inventory.link',
        id: 'inventoryMenuLink',
      };

    case MenuConstants.PEOPLE_ALL:
      return {
        key: MenuConstants.ANALYTICS,
        path: paths.people.getPath({ user_role: 'all' }),
        message: 'web.menu.people.link',
        id: 'peopleMenuLink',
      };

    case MenuConstants.USERS:
    case MenuConstants.USERS_ALL:
      return {
        key: MenuConstants.USERS,
        path: paths.users.getPath({ user_role: 'all' }),
        message: 'web.menu.users.link',
        id: 'usersMenuLink',
      };

    case MenuConstants.USERS_AND_ACCESS:
      return {
        key: MenuConstants.USERS_AND_ACCESS,
        path: paths.usersAndAccess.getPath({ subpage: 'users' }),
        message: 'web.menu.users_and_access.link',
        id: 'usersAccessMenuLink',
      };

    case MenuConstants.GATEWAYS:
      return {
        key: MenuConstants.GATEWAYS,
        path: paths.gateways.path,
        message: 'web.menu.gateways.link',
        id: 'gatewaysMenuLink',
      };

    case MenuConstants.DEALER_ACCESS:
      return {
        key: MenuConstants.DEALER_ACCESS,
        path: paths.dealerAccess.path,
        message: 'web.menu.dealer_access.link',
        id: 'dealerAccessMenu',
      };

    case MenuConstants.DEALER_ACCESS_EXTERNAL_LINK:
      return {
        key: MenuConstants.DEALER_ACCESS_EXTERNAL_LINK,
        outPath: urls.DEALERS_INFO_SITE_URL,
        message: 'web.menu.dealer_access.link',
        id: 'dealerAccessMenuLink',
        iconRight: 'action.open_in_new_window',
      };

    case MenuConstants.LEGAL:
      return {
        key: MenuConstants.LEGAL,
        path: paths.legal.path,
        message: 'web.menu.legal.link',
        id: 'legalMenuLink',
      };

    case MenuConstants.COMPANY_ACCOUNT:
      return {
        key: MenuConstants.COMPANY_ACCOUNT,
        path: paths.companySettings.getPath({
          subpage: 'account',
        }),
        message: 'web.menu.company_information.link',
        id: 'companyAccountMenuLink',
        isOnlyActiveWhenMatchingAll: true,
      };

    case MenuConstants.UPGRADE_SUBSCRIPTION:
      return {
        key: MenuConstants.UPGRADE_SUBSCRIPTION,
        path: paths.companySettings.getPath({
          subpage: 'subscription',
        }),
        message: 'web.menu.upgrade_subscription.link',
        id: 'subscriptionMenuLink',
        isOnlyActiveWhenMatchingAll: true,
      };

    case MenuConstants.DATA_EXPORT:
      return {
        key: MenuConstants.DATA_EXPORT,
        path: paths.dataExports.path,
        message: 'web.menu.data_exports.link',
        id: 'dataExportMenuLink',
      };

    case MenuConstants.ACCOUNT:
      return {
        key: MenuConstants.ACCOUNT,
        iconLeft: 'action.avatar_outlined',
        message: 'web.menu.account.link',
        id: 'accountMenuLink',
      };

    case MenuConstants.INFORMATION:
      return {
        key: MenuConstants.INFORMATION,
        message: 'web.menu.information.link',
        icon: 'notification.info_outlined',
        activeIcon: 'notification.info',
      };

    case MenuConstants.HOW_TO_VIDEOS:
      return {
        key: MenuConstants.HOW_TO_VIDEOS,
        message: 'web.menu.how_to_videos.link',
        id: 'howtoMenuLink',
      };

    case MenuConstants.FLEET_SERVICES:
      return {
        key: MenuConstants.FLEET_SERVICES,
        outPath: urls.FLEET_INFO_SITE_URL,
        label: 'Fleet Services',
        id: 'fleetServicesLink',
      };

    case MenuConstants.CHANGELOG:
      return {
        key: MenuConstants.CHANGELOG,
        path: paths.changelog.path,
        message: 'web.menu.changelog.link',
        id: 'changeLogLink',
      };

    case MenuConstants.SUPPORT:
      return {
        key: MenuConstants.SUPPORT,
        outPath: urls.SUPPORT_URL,
        message: 'web.menu.support.link',
        id: 'supportLink',
      };

    case MenuConstants.USER:
      return {
        key: MenuConstants.USER,
      };

    case MenuConstants.LOG_OUT:
      return {
        key: MenuConstants.LOG_OUT,
        path: paths.logout.path,
        iconLeft: 'action.log_out',
        message: 'web.menu.logout.link',
        id: 'logoutLink',
      };

    case MenuConstants.COMPANY:
      return {
        key: MenuConstants.COMPANY,
        path: '',
        message: 'web.menu.company.link',
        id: 'companyLink',
      };

    case MenuConstants.ENABLE_ACCESS_GROUPS:
      return {
        key: MenuConstants.ENABLE_ACCESS_GROUPS,
        path: paths.enableUsersAndAccess.path,
        message: 'web.menu.enable_access_groups.link',
        id: 'enableAccessGroupsLink',
      };

    default:
      return undefined;
  }
};

export const getMenuLabel = (mappedMenuItem: MappedMenuItem): React.ReactNode => {
  if (mappedMenuItem.label) {
    return mappedMenuItem.label;
  }

  return <FormattedMessageWrapper langKey={mappedMenuItem.message as string} />;
};

export const isMultiMenuItem = (menuItem: MenuItem): menuItem is MultiMenuItem =>
  !!(menuItem as MultiMenuItem).items;

export const hasMenuMetadata = (menuItem: HudskitMenuItem): menuItem is FleetMenuItem =>
  (menuItem as FleetMenuItem).metadata !== undefined;

export const isLinkMenuItem = (menuItem: MappedMenuItem): menuItem is MappedLinkMenuItem =>
  !!(menuItem as MappedLinkMenuItem).path;

export const isIconMenu = (menuItem: MappedMenuItem): menuItem is IconMenuItem =>
  !!(menuItem as IconMenuItem).icon;

export const isOutLinkMenuItem = (menuItem: MappedMenuItem): menuItem is MappedOutLinkMenuItem =>
  !!(menuItem as MappedOutLinkMenuItem).outPath;

const getTrackingLabel = (menu: MappedMenuItem) => {
  if (isLinkMenuItem(menu)) {
    return menu.path;
  }
  if (isOutLinkMenuItem(menu)) {
    return menu.outPath;
  }
  return menu.id;
};

const mapNavDropdownMenu = (menuItem: MenuItem, index: number): FleetMenuItem | undefined => {
  const mappedMenu = mapMenuConstants(menuItem);
  if (mappedMenu) {
    const tracking = getTrackingLabel(mappedMenu);
    return {
      id: index,
      label:
        mappedMenu.key === MenuConstants.COMPANY_ACCOUNT ? (
          <CompanyInfoLabel />
        ) : (
          <MenuLabel id={mappedMenu.id} trackingLabel={tracking}>
            {getMenuLabel(mappedMenu)}
          </MenuLabel>
        ),
      metadata: mappedMenu,
      iconLeft: mappedMenu.iconLeft,
      iconRight: mappedMenu.iconRight,
    };
  }
};

export const mapNavMenu = (menuItem: MenuItem, index: number): FleetMenuItem | undefined => {
  const mappedMenu = mapMenuConstants(menuItem);
  if (mappedMenu) {
    const tracking = getTrackingLabel(mappedMenu);
    const navMenu: FleetMenuItem = {
      id: index,
      label: (
        <MenuLabel id={mappedMenu.id} trackingLabel={tracking}>
          {getMenuLabel(mappedMenu)}
        </MenuLabel>
      ),
      metadata: mappedMenu,
    };
    if (isMultiMenuItem(menuItem)) {
      navMenu.menuItems = [];
      for (const submenuItem of menuItem.items) {
        const mappedSubmenu = mapNavDropdownMenu(submenuItem, index++);
        if (mappedSubmenu) {
          navMenu.menuItems.push(mappedSubmenu);
        }
      }
      navMenu.id = index;
    }
    return navMenu;
  }
};

export const composeLeftSection = (permissions: string[]): MenuItem[] => {
  const result: MenuItem[] = [
    {
      id: MenuConstants.DASHBOARD,
    },
  ];
  if (checkPermission(permissions, PermissionEnum.ASSET_READ)) {
    result.push({
      id: MenuConstants.INVENTORY,
    });
  }

  return result;
};

export const composeRightSection = (permissions: string[], isUTCmarket?: boolean): MenuItem[] => {
  const uptimeView = getDiamGroupId() !== undefined;
  if (uptimeView) {
    return [];
  }
  const result: MenuItem[] = [];

  if (checkPermission(permissions, PermissionEnum.USER_READ)) {
    if (checkPermission(permissions, PermissionEnum.ACCESS_GROUP_READ)) {
      result.push({
        id: MenuConstants.USERS_AND_ACCESS,
      });
    } else {
      result.push({
        id: MenuConstants.USERS,
      });
    }
  }

  const companyNode: MenuItem = {
    id: MenuConstants.COMPANY,
    items: [],
  };
  if (checkPermission(permissions, PermissionEnum.GATEWAY_READ)) {
    companyNode.items.push({
      id: MenuConstants.GATEWAYS,
    });
  }
  if (checkPermission(permissions, PermissionEnum.REPORT_READ)) {
    companyNode.items.push({
      id: MenuConstants.DATA_EXPORT,
    });
  }
  if (checkPermission(permissions, PermissionEnum.COMPANY_READ)) {
    companyNode.items.unshift({
      id: MenuConstants.COMPANY_ACCOUNT,
    });
  }
  if (checkPermission(permissions, PermissionEnum.TRUSTED_DEALER_READ)) {
    if (isUTCmarket) {
      companyNode.items.push({
        id: MenuConstants.DEALER_ACCESS,
      });
    } else {
      companyNode.items.push({
        id: MenuConstants.DEALER_ACCESS_EXTERNAL_LINK,
      });
    }
  }
  companyNode.items.push({
    id: MenuConstants.LEGAL,
  });
  if (companyNode.items.length > 0) {
    result.push(companyNode);
  }

  result.push({
    id: MenuConstants.INFORMATION,
    items: [
      {
        id: MenuConstants.HOW_TO_VIDEOS,
      },
      {
        id: MenuConstants.FLEET_SERVICES,
      },
      {
        id: MenuConstants.CHANGELOG,
      },
      {
        id: MenuConstants.SUPPORT,
      },
    ],
  });

  const userNode: MenuItem = {
    id: MenuConstants.USER,
    items: [],
  };
  if (checkPermission(permissions, PermissionEnum.USER_PROFILE_READ)) {
    userNode.items.push({
      id: MenuConstants.ACCOUNT,
    });
  }
  userNode.items.push({
    id: MenuConstants.LOG_OUT,
  });
  result.push(userNode);
  return result;
};
